import { z } from "zod";
import { SCRIPT_ROLE_SCORE_KEYS } from "./scriptRoleScoring";

export const scriptRolePersonaOutputSchema = z.object({
  personaSummary: z.string(),
  toneTags: z.array(z.string()).default([]),
  topicTags: z.array(z.string()).default([]),
  sampleQuotes: z.array(z.string()).default([]),
});

export const scriptRolePersonaSchema = scriptRolePersonaOutputSchema.extend({
  tempRoleId: z.string(),
});

export const scriptRolePersonaBatchSchema = z.object({
  personas: z.array(scriptRolePersonaSchema),
});

export const roleScoreDimensionInputSchema = z.object({
  key: z.enum(SCRIPT_ROLE_SCORE_KEYS),
  score: z.number(),
  reason: z.string().min(1),
});

export const roleAssignmentLlmItemSchema = z.object({
  tempRoleId: z.string(),
  number: z
    .string()
    .min(1, "编号不能为空")
    .describe("候选气氛员编号字符串，必须原样复制并保留前导零"),
  reason: z.string(),
  scoreBreakdown: z.array(roleScoreDimensionInputSchema).length(SCRIPT_ROLE_SCORE_KEYS.length),
});

export const roleAssignmentLlmResultSchema = z.object({
  assignments: z.array(roleAssignmentLlmItemSchema),
});

export const singleRoleAssignmentLlmSchema = roleAssignmentLlmItemSchema;

/** 打分阶段保留的候选人数；选择阶段纯规则筛选，池子越大越少触发 LLM 兜底 */
export const SCRIPT_ROLE_RANKED_CANDIDATE_COUNT = 10;
export const SCRIPT_ROLE_EXPANDED_CANDIDATE_COUNT = 10;
export const SCRIPT_ROLE_MAX_CANDIDATE_COUNT =
  SCRIPT_ROLE_RANKED_CANDIDATE_COUNT + SCRIPT_ROLE_EXPANDED_CANDIDATE_COUNT;

export const roleRankedCandidateLlmSchema = z.object({
  number: z
    .string()
    .min(1, "编号不能为空")
    .describe("候选气氛员编号字符串，必须原样复制并保留前导零"),
  score: z.number().min(0).max(100),
  reason: z.string(),
});

export const roleRankedAssignmentLlmSchema = z.object({
  tempRoleId: z.string(),
  rankedCandidates: z
    .array(roleRankedCandidateLlmSchema)
    .min(1)
    .max(SCRIPT_ROLE_RANKED_CANDIDATE_COUNT),
});

export const roleAssignmentItemSchema = z.object({
  tempRoleId: z.string(),
  wxid: z.string(),
  number: z.string(),
  nickname: z.string(),
  reason: z.string(),
  scoreBreakdown: z.array(roleScoreDimensionInputSchema).length(SCRIPT_ROLE_SCORE_KEYS.length),
  score: z.number(),
  /** 匹配来源；保留旧值用于历史缓存兼容。 */
  selectionSource: z
    .enum(["ranked", "fallback", "global", "global_expanded"])
    .optional(),
});

export const roleAssignmentResultSchema = z.object({
  assignments: z.array(roleAssignmentItemSchema),
});

export const singleRoleAssignmentSchema = roleAssignmentItemSchema;

export type ScriptRoleSelectionSource =
  | "ranked"
  | "fallback"
  | "global"
  | "global_expanded";

export const SCRIPT_ROLE_SELECTION_SOURCE_LABELS: Record<ScriptRoleSelectionSource, string> = {
  ranked: "规则直选",
  fallback: "二次 LLM",
  global: "全局分配",
  global_expanded: "扩容分配",
};

export type ScriptRolePersona = z.infer<typeof scriptRolePersonaSchema>;
export type RoleScoreDimensionInput = z.infer<typeof roleScoreDimensionInputSchema>;
export type RoleAssignmentLlmItemInput = z.infer<typeof roleAssignmentLlmItemSchema>;
export type RoleRankedCandidateLlmInput = z.infer<typeof roleRankedCandidateLlmSchema>;
export type RoleRankedAssignmentLlmInput = z.infer<typeof roleRankedAssignmentLlmSchema>;
export type RoleAssignmentItemInput = z.infer<typeof roleAssignmentItemSchema>;
export type RoleAssignmentResult = z.infer<typeof roleAssignmentResultSchema>;

export interface RankedRoleCandidate {
  number: string;
  /** 服务端按匹配时成员池解析出的稳定身份；旧缓存可能缺失。 */
  wxid?: string;
  score: number;
  reason: string;
  /** 旧缓存兼容；新管线只为最终选中的配对生成六维评分。 */
  scoreBreakdown?: RoleAssignmentItem["scoreBreakdown"];
  source?: "initial" | "expanded";
}

export interface MemberForRoleSelection {
  wxid: string;
  number: string;
  nickname: string;
}

function normalizeNumericMemberNumber(number: string) {
  return /^\d+$/.test(number) ? number.replace(/^0+(?=\d)/, "") : undefined;
}

/**
 * 将模型可能丢失前导零的编号映射回成员真实编号。
 * 精确编号优先；只有数字别名唯一时才建立映射，避免 "04"/"004" 错配。
 */
export function buildMemberNumberCanonicalLookup(numbers: string[]) {
  const canonicalNumbers = new Set(
    numbers.map((number) => number.trim()).filter(Boolean)
  );
  const lookup = new Map(
    [...canonicalNumbers].map((number) => [number, number] as const)
  );
  const aliases = new Map<string, Set<string>>();

  for (const number of canonicalNumbers) {
    const alias = normalizeNumericMemberNumber(number);
    if (!alias || alias === number) continue;
    const candidates = aliases.get(alias) ?? new Set<string>();
    candidates.add(number);
    aliases.set(alias, candidates);
  }

  for (const [alias, candidates] of aliases) {
    if (!lookup.has(alias) && candidates.size === 1) {
      lookup.set(alias, [...candidates][0]!);
    }
  }
  return lookup;
}

export function filterMembersForCandidateExpansion<T extends { number: string }>(
  members: T[],
  existingCandidates: Array<Pick<RankedRoleCandidate, "number">>
) {
  const existingNumbers = new Set(
    existingCandidates.map((candidate) => candidate.number.trim())
  );
  return members.filter((member) => {
    const number = member.number.trim();
    return number.length > 0 && !existingNumbers.has(number);
  });
}

export function collectValidRankedCandidates(input: {
  tempRoleId: string;
  rankedCandidates: RoleRankedCandidateLlmInput[];
  members: MemberForRoleSelection[];
}): { candidates: RankedRoleCandidate[]; skippedNumbers: string[] } {
  const memberByNumber = new Map(
    input.members
      .filter((member) => member.number.trim())
      .map((member) => [member.number.trim(), member])
  );
  const canonicalNumberLookup = buildMemberNumberCanonicalLookup([
    ...memberByNumber.keys(),
  ]);
  const seenNumbers = new Set<string>();
  const candidates: RankedRoleCandidate[] = [];
  const skippedNumbers: string[] = [];

  for (const candidate of input.rankedCandidates) {
    const rawNumber = candidate.number.trim();
    if (!rawNumber) {
      skippedNumbers.push("(空编号)");
      continue;
    }
    const number = canonicalNumberLookup.get(rawNumber);
    if (!number) {
      skippedNumbers.push(rawNumber);
      continue;
    }
    if (seenNumbers.has(number)) {
      skippedNumbers.push(number);
      continue;
    }
    seenNumbers.add(number);
    candidates.push({
      number,
      wxid: memberByNumber.get(number)?.wxid,
      reason: candidate.reason.trim(),
      score: candidate.score,
    });
  }

  return { candidates, skippedNumbers };
}

/** 扩容阶段 LLM 反复返回无效编号时，从可选池按编号顺序补足。 */
export function buildDeterministicExpansionCandidates(input: {
  eligibleMembers: MemberForRoleSelection[];
  existingCandidates: RankedRoleCandidate[];
  maxCount: number;
}): RankedRoleCandidate[] {
  const usedNumbers = new Set(input.existingCandidates.map((candidate) => candidate.number));
  const additional: RankedRoleCandidate[] = [];

  const sortedEligible = [...input.eligibleMembers].sort((left, right) =>
    left.number.localeCompare(right.number, undefined, { numeric: true })
  );

  for (const member of sortedEligible) {
    if (additional.length >= input.maxCount) break;
    const number = member.number.trim();
    if (!number || usedNumbers.has(number)) continue;
    usedNumbers.add(number);
    additional.push({
      number,
      wxid: member.wxid,
      score: 40,
      reason: "规则补充扩容候选",
    });
  }

  return additional;
}

export function validateRankedAssignmentOutput(input: {
  tempRoleId: string;
  rankedCandidates: RoleRankedCandidateLlmInput[];
  members: MemberForRoleSelection[];
}): RankedRoleCandidate[] {
  const memberByNumber = new Map(
    input.members
      .filter((member) => member.number.trim())
      .map((member) => [member.number.trim(), member])
  );
  const canonicalNumberLookup = buildMemberNumberCanonicalLookup([
    ...memberByNumber.keys(),
  ]);
  const seenNumbers = new Set<string>();
  const validated: RankedRoleCandidate[] = [];

  for (const candidate of input.rankedCandidates) {
    const rawNumber = candidate.number.trim();
    if (!rawNumber) {
      throw new Error(`临时角色 ${input.tempRoleId} 的候选排名包含空编号`);
    }
    const number = canonicalNumberLookup.get(rawNumber);
    if (!number) {
      throw new Error(`临时角色 ${input.tempRoleId} 的候选排名包含无效编号：${rawNumber}`);
    }
    if (seenNumbers.has(number)) {
      throw new Error(`临时角色 ${input.tempRoleId} 的候选排名包含重复编号：${number}`);
    }
    seenNumbers.add(number);

    validated.push({
      number,
      wxid: memberByNumber.get(number)?.wxid,
      reason: candidate.reason.trim(),
      score: candidate.score,
    });
  }

  return validated;
}

export interface RoleAssignmentItem {
  tempRoleId: string;
  wxid: string;
  number: string;
  nickname: string;
  score: number;
  reason: string;
  contentBeat?: import("./scriptRoleDiversity").ScriptContentBeatType;
  selectionSource?: ScriptRoleSelectionSource;
  scoreBreakdown: Array<{
    key: (typeof SCRIPT_ROLE_SCORE_KEYS)[number];
    label: string;
    maxScore: number;
    score: number;
    reason: string;
  }>;
}
